/**
 * CurationTool Component
 *
 * Admin tool for creating and managing capsules.
 * Three-step wizard:
 * 1. Add Files - Upload score files and assign to layers
 * 2. Metadata - Fill in capsule metadata
 * 3. Preview & Save - Review and save the capsule
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Capsule, LayerName, LAYER_ORDER, LAYER_LABELS, ZoomRegion } from '../../types/capsule';
import { useCapsules } from '../../hooks/useCapsules';
import { IntroView } from '../../components/IntroView';
import { mergeMusicXmlParts } from '../../utils/musicXmlMerger';
import { extractBpmFromFilename } from '../../utils/bpmExtractor';
import { storeFileForSync, clearSyncFileStore, retrieveFileForSync } from '../../lib/syncFileStore';
import { uploadToCloudinary, shouldUseCloudinary } from '../../lib/cloudinaryUpload';
import { retryUpload } from '../../lib/retryUpload';
import '../../styles/CurationTool.css';

/**
 * Update the tempo in a MusicXML file content
 * @param xmlContent The MusicXML content as string
 * @param newTempo The new tempo (BPM) to set
 * @returns Updated XML content
 */
function updateMusicXmlTempo(xmlContent: string, newTempo: number): string {
  // MusicXML stores tempo in <per-minute> element within <metronome>
  // Replace any existing per-minute value with the new tempo
  const updatedContent = xmlContent.replace(
    /<per-minute>[\d.]+<\/per-minute>/g,
    `<per-minute>${newTempo}</per-minute>`
  );

  return updatedContent;
}

/**
 * Convert a File to base64 string
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Fetch a file from URL and convert to base64
 */
async function fetchFileAsBase64(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.status}`);
  }
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Detect media type from file extension
 */
function getMediaTypeFromFilename(filename: string): 'audio' | 'video' | null {
  const ext = filename.toLowerCase().slice(filename.lastIndexOf('.'));
  if (['.mp3', '.wav', '.ogg', '.m4a'].includes(ext)) return 'audio';
  if (['.mp4', '.webm', '.mov'].includes(ext)) return 'video';
  return null;
}

/**
 * Working state for a single layer during curation
 */
interface LayerWorkingState {
  file: File | null;
  fileName: string;
  existingFile: boolean;      // true if file exists on server
  existingFileUrl?: string;   // Full URL for existing file (Blob storage in production)
  // Unified media fields (audio OR video)
  mediaFile: File | null;
  mediaFileName: string;
  mediaType: 'audio' | 'video' | null;
  existingMedia: boolean;     // true if media exists on server
  existingMediaUrl?: string;  // Full URL for existing media (Blob storage in production)
  tempo: number | null;
  mediaOffset: number;        // Works for both audioOffset and videoOffset
  // Video-specific: zoom regions
  zoomRegions?: {
    rightHand?: { x: number; y: number; width: number; height: number };
    leftHand?: { x: number; y: number; width: number; height: number };
  };
}

/**
 * Complete working state for a capsule during curation
 */
interface CapsuleWorkingState {
  name: string;
  slug: string;
  layers: Partial<Record<LayerName, LayerWorkingState>>;
  metadata: {
    title: string;
    artist: string;
    composer: string;
    arranger: string;
    difficulty: number;
    tags: string;
    notes: string;
  };
  /** Intro layer content (optional video + description) */
  intro: {
    videoFile: File | null;
    videoFileName: string;
    existingVideo: boolean;
    existingVideoUrl?: string;
    description: string;
  };
}

type Step = 'intro' | 'files' | 'metadata' | 'preview';

/**
 * Generate URL-safe slug from title
 */
function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

/**
 * Initial empty capsule state
 */
function createEmptyState(): CapsuleWorkingState {
  return {
    name: '',
    slug: '',
    layers: {},
    metadata: {
      title: '',
      artist: '',
      composer: '',
      arranger: '',
      difficulty: 3,
      tags: '',
      notes: '',
    },
    intro: {
      videoFile: null,
      videoFileName: '',
      existingVideo: false,
      description: '',
    },
  };
}

const DRAFT_STORAGE_KEY = 'fingerstyle-curation-draft';

/**
 * Serialize capsule state for localStorage (strips non-serializable File objects)
 */
function serializeCapsuleForStorage(state: CapsuleWorkingState): string {
  const serializable = {
    ...state,
    layers: Object.fromEntries(
      Object.entries(state.layers).map(([key, layer]) => [
        key,
        layer ? { ...layer, file: null, mediaFile: null } : layer,
      ])
    ),
    intro: {
      ...state.intro,
      videoFile: null,
    },
  };
  return JSON.stringify(serializable);
}

/**
 * Load draft from localStorage
 */
function loadDraft(): CapsuleWorkingState | null {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CapsuleWorkingState;
  } catch {
    return null;
  }
}

/**
 * Clear saved draft from localStorage
 */
function clearDraft(): void {
  localStorage.removeItem(DRAFT_STORAGE_KEY);
}

/**
 * Validation errors for the curation form
 */
interface ValidationErrors {
  title?: string;
  layers?: string;
}

export function CurationTool() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editSlug = searchParams.get('edit');
  const isEditMode = Boolean(editSlug);

  const { getCapsuleBySlug, loading: loadingCapsule } = useCapsules();

  const [currentStep, setCurrentStep] = useState<Step>('intro');
  const [capsule, setCapsule] = useState<CapsuleWorkingState>(createEmptyState());
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [mergeNotification, setMergeNotification] = useState<string | null>(null);
  const mergeNotificationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const [mediaTargetLayer, setMediaTargetLayer] = useState<LayerName | null>(null);
  const [replacingLayer, setReplacingLayer] = useState<LayerName | null>(null);
  const replaceFileInputRef = useRef<HTMLInputElement>(null);

  const introVideoInputRef = useRef<HTMLInputElement>(null);

  // --- Draft auto-save ---
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasLoadedInitialData = useRef(false);

  // Debounced save to localStorage whenever capsule changes
  useEffect(() => {
    // Don't save until initial data is loaded (prevents overwriting draft with empty state)
    if (!hasLoadedInitialData.current) return;

    if (draftTimerRef.current) {
      clearTimeout(draftTimerRef.current);
    }
    draftTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_STORAGE_KEY, serializeCapsuleForStorage(capsule));
      } catch {
        // localStorage full or unavailable - silently ignore
      }
    }, 2000);

    return () => {
      if (draftTimerRef.current) {
        clearTimeout(draftTimerRef.current);
      }
    };
  }, [capsule]);

  // Check for saved draft on mount (only for new capsules, not edit mode)
  useEffect(() => {
    if (isEditMode) {
      hasLoadedInitialData.current = true;
      return;
    }
    const draft = loadDraft();
    if (draft && (draft.metadata.title || Object.keys(draft.layers).length > 0)) {
      setShowDraftBanner(true);
    } else {
      hasLoadedInitialData.current = true;
    }
  }, [isEditMode]);

  const handleRestoreDraft = useCallback(() => {
    const draft = loadDraft();
    if (draft) {
      setCapsule(draft);
    }
    setShowDraftBanner(false);
    hasLoadedInitialData.current = true;
  }, []);

  const handleDiscardDraft = useCallback(() => {
    clearDraft();
    setShowDraftBanner(false);
    hasLoadedInitialData.current = true;
  }, []);

  // --- Validation ---
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  const clearValidationError = useCallback((field: keyof ValidationErrors) => {
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // --- Upload progress (per-file) ---
  const [fileUploadProgress, setFileUploadProgress] = useState<Record<string, number>>({});
  const [retryStatus, setRetryStatus] = useState<string | null>(null);

  const handleIntroVideoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const mediaType = getMediaTypeFromFilename(file.name);
    if (mediaType !== 'video') {
      alert('Please select a video file (MP4, WEBM, or MOV).');
      if (introVideoInputRef.current) introVideoInputRef.current.value = '';
      return;
    }
    setCapsule((prev) => ({
      ...prev,
      intro: { ...prev.intro, videoFile: file, videoFileName: file.name, existingVideo: false },
    }));
    if (introVideoInputRef.current) introVideoInputRef.current.value = '';
  };

  const handleRemoveIntroVideo = () => {
    setCapsule((prev) => ({
      ...prev,
      intro: { ...prev.intro, videoFile: null, videoFileName: '', existingVideo: false, existingVideoUrl: undefined },
    }));
  };

  const handleIntroDescriptionChange = (value: string) => {
    setCapsule((prev) => ({
      ...prev,
      intro: { ...prev.intro, description: value },
    }));
  };

  const steps: { id: Step; label: string; number: number }[] = [
    { id: 'intro', label: 'Intro', number: 1 },
    { id: 'files', label: 'Add Files', number: 2 },
    { id: 'metadata', label: 'Metadata', number: 3 },
    { id: 'preview', label: 'Preview & Save', number: 4 },
  ];

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    let file = event.target.files?.[0];
    if (!file) return;

    // Check if this is a MusicXML file
    const isMusicXml = file.name.toLowerCase().endsWith('.xml') ||
                       file.name.toLowerCase().endsWith('.musicxml');

    if (isMusicXml) {
      try {
        // Read file as text and try to merge notation + TAB parts
        const text = await file.text();
        const mergeResult = mergeMusicXmlParts(text);

        // Clear any existing timeout and show notification
        if (mergeNotificationTimeoutRef.current) {
          clearTimeout(mergeNotificationTimeoutRef.current);
        }
        setMergeNotification(mergeResult.message);
        mergeNotificationTimeoutRef.current = setTimeout(() => setMergeNotification(null), 4000);

        if (mergeResult.wasMerged) {
          // Create a new file from the merged content
          const mergedBlob = new Blob([mergeResult.xmlContent], { type: 'application/xml' });
          file = new File([mergedBlob], file.name, { type: 'application/xml' });
        }
      } catch (error) {
        console.error('Failed to process MusicXML file:', error);
        if (mergeNotificationTimeoutRef.current) {
          clearTimeout(mergeNotificationTimeoutRef.current);
        }
        setMergeNotification('Failed to read MusicXML file');
        mergeNotificationTimeoutRef.current = setTimeout(() => setMergeNotification(null), 4000);
        // Continue with original file
      }
    }

    setSelectedFile(file);
  };

  const handleAssignToLayer = (layer: LayerName) => {
    if (!selectedFile) return;

    clearValidationError('layers');

    setCapsule((prev) => ({
      ...prev,
      layers: {
        ...prev.layers,
        [layer]: {
          file: selectedFile,
          fileName: selectedFile.name,
          existingFile: false,
          mediaFile: null,
          mediaFileName: '',
          mediaType: null,
          existingMedia: false,
          tempo: null,
          mediaOffset: 0,
        },
      },
    }));

    // Clear selection after assignment
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveLayer = (layer: LayerName) => {
    setCapsule((prev) => {
      const newLayers = { ...prev.layers };
      delete newLayers[layer];
      return { ...prev, layers: newLayers };
    });
  };

  const handleMediaSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && mediaTargetLayer) {
      const mediaType = getMediaTypeFromFilename(file.name);
      if (!mediaType) {
        alert('Unsupported format. Use MP3, WAV, OGG, M4A for audio or MP4, WEBM, MOV for video.');
        if (mediaInputRef.current) {
          mediaInputRef.current.value = '';
        }
        return;
      }

      // Check file size limit (100MB for Cloudinary unsigned uploads)
      const MAX_FILE_SIZE_MB = 100;
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > MAX_FILE_SIZE_MB) {
        alert(`File is too large (${fileSizeMB.toFixed(1)}MB). Maximum size is ${MAX_FILE_SIZE_MB}MB. Please compress the video before uploading.`);
        if (mediaInputRef.current) {
          mediaInputRef.current.value = '';
        }
        return;
      }

      // Extract BPM from filename (e.g., "video95bpm.mp4" -> 95)
      const extractedTempo = extractBpmFromFilename(file.name);



      setCapsule((prev) => ({
        ...prev,
        layers: {
          ...prev.layers,
          [mediaTargetLayer]: {
            ...prev.layers[mediaTargetLayer]!,
            mediaFile: file,
            mediaFileName: file.name,
            mediaType,
            existingMedia: false,
            tempo: extractedTempo,
          },
        },
      }));
      setMediaTargetLayer(null);
      if (mediaInputRef.current) {
        mediaInputRef.current.value = '';
      }
    }
  };

  const handleAddMediaClick = (layer: LayerName) => {
    setMediaTargetLayer(layer);
    mediaInputRef.current?.click();
  };

  const handleSyncClick = async (layer: LayerName) => {
    const layerData = capsule.layers[layer];
    if (!layerData) return;

    // Need either a File object or existing file on server
    const hasScore = layerData.file || layerData.existingFile;
    // Need either media File object or existing media on server (audio only for sync)
    const hasMedia = layerData.mediaFile || layerData.existingMedia;

    if (!hasScore || !hasMedia) {
      return;
    }

    const isVideo = layerData.mediaType === 'video';

    try {
      let scoreBase64: string;
      let scoreFileName: string;

      if (layerData.file) {
        // Use the File object directly
        scoreBase64 = await fileToBase64(layerData.file);
        scoreFileName = layerData.fileName;
      } else if (layerData.existingFile && layerData.existingFileUrl) {
        // Fetch existing file from server using stored URL (works for both Blob and local)
        const fileUrl = layerData.existingFileUrl.startsWith('http')
          ? layerData.existingFileUrl
          : `/scores_library/${editSlug}/${layerData.existingFileUrl}`;
        scoreBase64 = await fetchFileAsBase64(fileUrl);
        scoreFileName = layerData.fileName;
      } else {
        console.error('[CurationTool] No score file available');
        return;
      }

      let mediaFile: File | null = null;
      let mediaBlob: Blob | null = null;
      let mediaFileName: string;

      if (layerData.mediaFile) {
        // Use the File object directly
        mediaFile = layerData.mediaFile;
        mediaFileName = layerData.mediaFileName;
      } else if (layerData.existingMedia && layerData.existingMediaUrl) {
        // Fetch existing media from server
        const mediaUrl = layerData.existingMediaUrl.startsWith('http')
          ? layerData.existingMediaUrl
          : `/scores_library/${editSlug}/${layerData.existingMediaUrl}`;
        const response = await fetch(mediaUrl);
        if (!response.ok) throw new Error(`Failed to fetch media: ${response.status}`);
        mediaBlob = await response.blob();
        mediaFileName = layerData.mediaFileName;
      } else {
        console.error('[CurationTool] No media file available');
        return;
      }

      const syncState: Record<string, unknown> = {
        layer,
        scoreFile: { name: scoreFileName, data: scoreBase64 },
        initialOffset: layerData.mediaOffset || 0,
        capsuleSlug: editSlug || undefined,
      };

      // Pass as audioFile or videoFile based on media type
      if (isVideo) {
        // Video files are too large for sessionStorage - use in-memory file store
        // Clear any previous files first to prevent memory buildup
        clearSyncFileStore();
        const videoFileOrBlob = mediaFile || new File([mediaBlob!], mediaFileName, { type: mediaBlob!.type });
        storeFileForSync('videoFile', mediaFileName, videoFileOrBlob);
        // Store marker in sessionStorage instead of actual data
        syncState.videoFile = { name: mediaFileName, data: '__FILE_STORE__' };
        syncState.initialZoomRegions = layerData.zoomRegions;
      } else {
        // Audio files are smaller - use base64 encoding
        const audioFileOrBlob = mediaFile || new File([mediaBlob!], mediaFileName, { type: mediaBlob!.type });
        const mediaBase64 = await fileToBase64(audioFileOrBlob);
        syncState.audioFile = { name: mediaFileName, data: mediaBase64 };
      }

      sessionStorage.setItem('syncViewState', JSON.stringify(syncState));

      // Save the entire working state so we can restore it when returning
      // Note: File objects can't be JSON serialized, so we need to clear them
      // The files are already saved separately in syncViewState
      const capsuleForStorage = {
        ...capsule,
        layers: Object.fromEntries(
          Object.entries(capsule.layers).map(([key, layer]) => [
            key,
            layer ? { ...layer, file: null, mediaFile: null } : layer,
          ])
        ),
      };
      sessionStorage.setItem('curationWorkingState', JSON.stringify(capsuleForStorage));

      navigate(`/admin/curate/sync?layer=${layer}`);
    } catch (error) {
      console.error('[CurationTool] Failed to prepare sync:', error);
      alert('Failed to prepare files for sync. Please try again.');
    }
  };

  const handleRemoveMedia = (layer: LayerName) => {
    setCapsule((prev) => ({
      ...prev,
      layers: {
        ...prev.layers,
        [layer]: {
          ...prev.layers[layer]!,
          mediaFile: null,
          mediaFileName: '',
          mediaType: null,
          existingMedia: false,
          mediaOffset: 0,
          zoomRegions: undefined,
        },
      },
    }));
  };

  const handleReplaceClick = (layer: LayerName) => {
    setReplacingLayer(layer);
    replaceFileInputRef.current?.click();
  };

  const handleReplaceFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && replacingLayer) {
      setCapsule((prev) => ({
        ...prev,
        layers: {
          ...prev.layers,
          [replacingLayer]: {
            ...prev.layers[replacingLayer]!,
            file: file,
            fileName: file.name,
            existingFile: false,
          },
        },
      }));
    }
    setReplacingLayer(null);
    if (replaceFileInputRef.current) {
      replaceFileInputRef.current.value = '';
    }
  };

  const handleUndoReplace = (layer: LayerName) => {
    const existingCapsule = editSlug ? getCapsuleBySlug(editSlug) : null;
    const originalContent = existingCapsule?.layers[layer];

    if (originalContent) {
      setCapsule((prev) => ({
        ...prev,
        layers: {
          ...prev.layers,
          [layer]: {
            ...prev.layers[layer]!,
            file: null,
            fileName: originalContent.file,
            existingFile: true,
          },
        },
      }));
    }
  };

  const handleMetadataChange = (
    field: keyof CapsuleWorkingState['metadata'],
    value: string | number
  ) => {
    // Clear title validation error when user types
    if (field === 'title' && typeof value === 'string' && value.trim() !== '') {
      clearValidationError('title');
    }

    setCapsule((prev) => {
      const newMetadata = { ...prev.metadata, [field]: value };

      // Auto-generate slug when title changes
      if (field === 'title' && typeof value === 'string') {
        return {
          ...prev,
          metadata: newMetadata,
          slug: generateSlug(value),
        };
      }

      return { ...prev, metadata: newMetadata };
    });
  };

  const handleSlugChange = (slug: string) => {
    setCapsule((prev) => ({
      ...prev,
      slug: generateSlug(slug),
    }));
  };

  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  /**
   * Validate form before save. Returns true if valid.
   */
  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};

    if (!capsule.metadata.title.trim()) {
      errors.title = 'Title is required';
    }

    const nonIntroLayers = Object.keys(capsule.layers).filter((l) => l !== 'intro');
    const hasFileInLayer = nonIntroLayers.some((l) => {
      const layer = capsule.layers[l as LayerName];
      return layer && (layer.file || layer.existingFile);
    });
    if (!hasFileInLayer) {
      errors.layers = 'At least one layer must have a score file';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  /**
   * Upload a single file to Cloudinary with retry and progress tracking.
   */
  const uploadWithRetry = async (
    file: File,
    folder: string,
    progressKey: string
  ) => {
    setFileUploadProgress((prev) => ({ ...prev, [progressKey]: 0 }));
    setRetryStatus(null);

    const result = await retryUpload(
      () =>
        uploadToCloudinary(file, folder, (progress) => {
          setFileUploadProgress((prev) => ({ ...prev, [progressKey]: progress.percent }));
        }),
      3,
      (status) => {
        setRetryStatus(
          `Upload failed for ${file.name}, retrying (${status.attempt}/${status.maxRetries})...`
        );
        // Reset progress for retry
        setFileUploadProgress((prev) => ({ ...prev, [progressKey]: 0 }));
      }
    );

    setRetryStatus(null);
    setFileUploadProgress((prev) => {
      const next = { ...prev };
      delete next[progressKey];
      return next;
    });

    return result;
  };

  const handleSave = async (isDraft = false) => {
    // Validate - for draft, only require title if provided
    if (!isDraft && !validateForm()) {
      return;
    }

    // For draft mode, minimal validation
    if (isDraft) {
      if (!capsule.metadata.title.trim()) {
        alert('Please enter a title before saving draft');
        return;
      }
      const hasAnyLayer = Object.keys(capsule.layers).some(l => {
        const layer = capsule.layers[l as LayerName];
        return layer && (layer.file || layer.existingFile);
      });
      if (!hasAnyLayer) {
        alert('Please add at least one layer before saving draft');
        return;
      }
    }

    setSaveError(null);

    // Determine if this is a new capsule or edit
    const isNew = !isEditMode;
    const useCloudinary = shouldUseCloudinary();

    // Build layers data and track files to upload
    const layersData: Record<string, {
      fileName: string;
      hasNewFile: boolean;
      description?: string;
      // Audio fields (when mediaType is 'audio')
      audioFileName?: string;
      hasNewAudio?: boolean;
      audioOffset?: number;
      // Video fields (when mediaType is 'video')
      videoFileName?: string;
      hasNewVideo?: boolean;
      videoOffset?: number;
      zoomRegions?: {
        rightHand?: { x: number; y: number; width: number; height: number };
        leftHand?: { x: number; y: number; width: number; height: number };
      };
      tempo: number | null;
      // Pre-uploaded URLs (for production Cloudinary uploads)
      preUploadedFileUrl?: string;
      preUploadedMediaUrl?: string;
    }> = {};

    // Include intro layer data if we have video or description
    if (capsule.intro.videoFile || capsule.intro.existingVideo || capsule.intro.description) {
      layersData['intro'] = {
        fileName: '__intro__',
        hasNewFile: false,
        tempo: null,
        description: capsule.intro.description || undefined,
        videoFileName: capsule.intro.videoFileName || undefined,
        hasNewVideo: capsule.intro.videoFile !== null,
        videoOffset: 0,
      };
    }

    try {
      // In production, upload large files to Cloudinary first
      if (useCloudinary) {
        for (const [layerName, layerState] of Object.entries(capsule.layers)) {
          if (!layerState) continue;

          // Initialize layer data
          layersData[layerName] = {
            fileName: layerState.fileName,
            hasNewFile: layerState.file !== null,
            tempo: layerState.tempo,
          };

          // Upload score file if new
          if (layerState.file) {
            let fileToUpload = layerState.file;

            // If this is a MusicXML file and we have a tempo from video, update the tempo
            const isMusicXml = layerState.fileName.toLowerCase().endsWith('.xml') ||
                               layerState.fileName.toLowerCase().endsWith('.musicxml');
            if (isMusicXml && layerState.tempo) {
              setUploadProgress(`Updating tempo in ${layerState.fileName}...`);
              const xmlContent = await layerState.file.text();
              const updatedXml = updateMusicXmlTempo(xmlContent, layerState.tempo);
              const updatedBlob = new Blob([updatedXml], { type: 'application/xml' });
              fileToUpload = new File([updatedBlob], layerState.fileName, { type: 'application/xml' });
            }

            setUploadProgress(`Uploading ${layerState.fileName}...`);
            const folder = `capsules/${capsule.slug}`;
            const result = await uploadWithRetry(fileToUpload, folder, `score_${layerName}`);
            layersData[layerName].preUploadedFileUrl = result.secureUrl;
          }

          // Add media-specific fields
          if (layerState.mediaType === 'audio') {
            layersData[layerName].audioFileName = layerState.mediaFileName;
            layersData[layerName].hasNewAudio = layerState.mediaFile !== null;
            layersData[layerName].audioOffset = layerState.mediaOffset;

            // Upload audio file if new
            if (layerState.mediaFile) {
              setUploadProgress(`Uploading ${layerState.mediaFileName}...`);
              const folder = `capsules/${capsule.slug}`;
              const result = await uploadWithRetry(layerState.mediaFile, folder, `audio_${layerName}`);
              layersData[layerName].preUploadedMediaUrl = result.secureUrl;
            }
          } else if (layerState.mediaType === 'video') {
            layersData[layerName].videoFileName = layerState.mediaFileName;
            layersData[layerName].hasNewVideo = layerState.mediaFile !== null;
            layersData[layerName].videoOffset = layerState.mediaOffset;
            if (layerState.zoomRegions) {
              layersData[layerName].zoomRegions = layerState.zoomRegions;
            }

            // Upload video file if new
            if (layerState.mediaFile) {
              setUploadProgress(`Uploading ${layerState.mediaFileName}...`);
              const folder = `capsules/${capsule.slug}`;
              const result = await uploadWithRetry(layerState.mediaFile, folder, `video_${layerName}`);
              layersData[layerName].preUploadedMediaUrl = result.secureUrl;
            }
          }
        }

        // Upload intro video to Cloudinary if new
        if (capsule.intro.videoFile) {
          setUploadProgress('Uploading intro video...');
          const folder = `capsules/${capsule.slug}`;
          const result = await uploadWithRetry(capsule.intro.videoFile, folder, 'intro_video');
          if (layersData['intro']) {
            layersData['intro'].preUploadedMediaUrl = result.secureUrl;
          }
        }

        setUploadProgress('Saving capsule metadata...');

        // Build capsule data JSON with pre-uploaded URLs
        const capsuleData = {
          slug: capsule.slug,
          isNew,
          metadata: {
            title: capsule.metadata.title,
            artist: capsule.metadata.artist,
            composer: capsule.metadata.composer,
            arranger: capsule.metadata.arranger,
            difficulty: capsule.metadata.difficulty,
            tags: capsule.metadata.tags,
            notes: capsule.metadata.notes,
          },
          layers: layersData,
        };

        // Send metadata only (files already uploaded)
        const response = await fetch('/api/capsules/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(capsuleData),
        });

        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.error || 'Failed to save capsule');
        }
      } else {
        // Local development: use FormData approach
        const formData = new FormData();

        for (const [layerName, layerState] of Object.entries(capsule.layers)) {
          if (!layerState) continue;

          // Base layer data
          const layerData: typeof layersData[string] = {
            fileName: layerState.fileName,
            hasNewFile: layerState.file !== null,
            tempo: layerState.tempo,
          };

          // Add media-specific fields based on type
          if (layerState.mediaType === 'audio') {
            layerData.audioFileName = layerState.mediaFileName;
            layerData.hasNewAudio = layerState.mediaFile !== null;
            layerData.audioOffset = layerState.mediaOffset;
          } else if (layerState.mediaType === 'video') {
            layerData.videoFileName = layerState.mediaFileName;
            layerData.hasNewVideo = layerState.mediaFile !== null;
            layerData.videoOffset = layerState.mediaOffset;
            if (layerState.zoomRegions) {
              layerData.zoomRegions = layerState.zoomRegions;
            }
          }

          layersData[layerName] = layerData;

          // Add files to FormData
          if (layerState.file) {
            let fileToUpload = layerState.file;

            // If this is a MusicXML file and we have a tempo from video, update the tempo
            const isMusicXml = layerState.fileName.toLowerCase().endsWith('.xml') ||
                               layerState.fileName.toLowerCase().endsWith('.musicxml');
            if (isMusicXml && layerState.tempo) {
              const xmlContent = await layerState.file.text();
              const updatedXml = updateMusicXmlTempo(xmlContent, layerState.tempo);
              const updatedBlob = new Blob([updatedXml], { type: 'application/xml' });
              fileToUpload = new File([updatedBlob], layerState.fileName, { type: 'application/xml' });
            }

            formData.append(`file_${layerName}`, fileToUpload);
          }
          if (layerState.mediaFile) {
            const mediaFieldName = layerState.mediaType === 'video'
              ? `video_${layerName}`
              : `audio_${layerName}`;
            formData.append(mediaFieldName, layerState.mediaFile);
          }
        }

        // Build capsule data JSON
        const capsuleData = {
          slug: capsule.slug,
          isNew,
          metadata: {
            title: capsule.metadata.title,
            artist: capsule.metadata.artist,
            composer: capsule.metadata.composer,
            arranger: capsule.metadata.arranger,
            difficulty: capsule.metadata.difficulty,
            tags: capsule.metadata.tags,
            notes: capsule.metadata.notes,
          },
          layers: layersData,
        };

        // Add intro video file to FormData
        if (capsule.intro.videoFile) {
          formData.append('video_intro', capsule.intro.videoFile);
        }

        formData.append('capsuleData', JSON.stringify(capsuleData));

        const response = await fetch('http://localhost:3001/api/capsules/save', {
          method: 'POST',
          body: formData,
        });

        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.error || 'Failed to save capsule');
        }
      }

      setUploadProgress(null);
      setFileUploadProgress({});

      // Clear draft on successful save
      clearDraft();

      // Success!
      alert(`Capsule "${capsule.metadata.title}" saved successfully!`);

      // Navigate to capsules list for new capsules
      if (isNew) {
        navigate('/admin/capsules');
      }

    } catch (err) {
      setUploadProgress(null);
      setFileUploadProgress({});
      setRetryStatus(null);
      console.error('Save error:', err);
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setSaveError(errorMsg);
    }
  };

  // Cleanup notification timeout on unmount
  useEffect(() => {
    return () => {
      if (mergeNotificationTimeoutRef.current) {
        clearTimeout(mergeNotificationTimeoutRef.current);
      }
    };
  }, []);

  // Track if we've already restored from sync to prevent duplicate restores
  const hasRestoredFromSync = useRef(false);

  // Load capsule data when in edit mode, or restore from sync if returning from SyncView
  useEffect(() => {
    if (!isEditMode || !editSlug || loadingCapsule) {
      return;
    }

    // Check if returning from sync
    const syncResult = sessionStorage.getItem('syncViewResult');
    const workingStateJson = sessionStorage.getItem('curationWorkingState');

    if (syncResult && workingStateJson && !hasRestoredFromSync.current) {
      // Returning from SyncView - restore working state and apply new offset
      // Mark as restored FIRST to prevent duplicate restores
      hasRestoredFromSync.current = true;

      // Get syncViewState BEFORE removing (we need to restore file data from it)
      const syncViewStateJson = sessionStorage.getItem('syncViewState');

      // Clean up sessionStorage
      sessionStorage.removeItem('syncViewResult');
      sessionStorage.removeItem('syncViewState');
      sessionStorage.removeItem('curationWorkingState');

      try {
        const { layer, offset, isVideoMode, zoomRegions } = JSON.parse(syncResult) as {
          layer: LayerName;
          offset: number;
          isVideoMode?: boolean;
          zoomRegions?: { leftHand?: ZoomRegion; rightHand?: ZoomRegion };
        };
        const savedWorkingState = JSON.parse(workingStateJson) as CapsuleWorkingState;

        // Restore file data from syncViewState (since File objects can't be JSON serialized)
        if (syncViewStateJson && savedWorkingState.layers[layer]) {
          const syncViewState = JSON.parse(syncViewStateJson);

          // Restore score file if it was a new file (not existing)
          if (syncViewState.scoreFile && !savedWorkingState.layers[layer].existingFile) {
            const { name, data } = syncViewState.scoreFile;
            // Convert base64 back to File
            const binary = atob(data);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
              bytes[i] = binary.charCodeAt(i);
            }
            const mimeType = name.endsWith('.xml') ? 'application/xml' : 'application/octet-stream';
            const restoredFile = new File([bytes], name, { type: mimeType });
            savedWorkingState.layers[layer].file = restoredFile;
          }

          // Restore media file if it was new
          // Handle video files (stored in memory via syncFileStore)
          if (syncViewState.videoFile && syncViewState.videoFile.data === '__FILE_STORE__') {
            const storedVideo = retrieveFileForSync('videoFile');
            if (storedVideo) {
              savedWorkingState.layers[layer].mediaFile = storedVideo.file;
              savedWorkingState.layers[layer].mediaFileName = storedVideo.name;
              savedWorkingState.layers[layer].mediaType = 'video';
              savedWorkingState.layers[layer].existingMedia = false;
            }
          }
          // Handle audio files (stored as base64 in sessionStorage)
          else if (syncViewState.audioFile && savedWorkingState.layers[layer].mediaFile === null) {
            const { name, data } = syncViewState.audioFile;
            const binary = atob(data);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
              bytes[i] = binary.charCodeAt(i);
            }
            // Determine MIME type from extension
            const ext = name.split('.').pop()?.toLowerCase() || '';
            const mimeType = ext === 'mp3' ? 'audio/mpeg' :
                            ext === 'mp4' ? 'video/mp4' :
                            ext === 'wav' ? 'audio/wav' : 'application/octet-stream';
            const restoredFile = new File([bytes], name, { type: mimeType });
            savedWorkingState.layers[layer].mediaFile = restoredFile;
            savedWorkingState.layers[layer].mediaType = 'audio';
          }
        }

        // Update the synced layer with the new offset (and zoom regions for video)
        if (savedWorkingState.layers[layer]) {
          savedWorkingState.layers[layer].mediaOffset = offset;
          // Update mediaType based on sync mode (user may have switched from audio to video)
          if (isVideoMode) {
            savedWorkingState.layers[layer].mediaType = 'video';
            if (zoomRegions) {
              savedWorkingState.layers[layer].zoomRegions = zoomRegions;
            }
          }
        }

        setCapsule(savedWorkingState);
      } catch (e) {
        console.error('Failed to restore sync state:', e);
      }
      return;
    }

    // Skip normal load if we already restored from sync
    if (hasRestoredFromSync.current) {
      return;
    }

    // Normal load from storage
    const existingCapsule = getCapsuleBySlug(editSlug);
    if (existingCapsule) {
      const layers: Partial<Record<LayerName, LayerWorkingState>> = {};
      for (const [layerKey, content] of Object.entries(existingCapsule.layers)) {
        const layer = layerKey as LayerName;
        // Use originalFileName if available, otherwise extract from URL
        const isFileUrl = content.file.startsWith('http');
        const fileName = content.originalFileName ||
          (isFileUrl ? decodeURIComponent(content.file.split('/').pop() || content.file) : content.file);

        // Determine media type and filename from existing content
        const hasVideo = Boolean(content.video);
        const hasAudio = Boolean(content.audio);
        let mediaFileName = '';
        let mediaUrl: string | undefined;
        let mediaType: 'audio' | 'video' | null = null;

        if (hasVideo) {
          const isVideoUrl = content.video?.startsWith('http');
          mediaFileName = content.originalVideoFileName ||
            (isVideoUrl ? decodeURIComponent(content.video?.split('/').pop() || content.video || '') : (content.video || ''));
          mediaUrl = content.video;
          mediaType = 'video';
        } else if (hasAudio) {
          const isAudioUrl = content.audio?.startsWith('http');
          mediaFileName = content.originalAudioFileName ||
            (isAudioUrl ? decodeURIComponent(content.audio?.split('/').pop() || content.audio || '') : (content.audio || ''));
          mediaUrl = content.audio;
          mediaType = 'audio';
        }

        // Extract tempo from manifest or from media filename
        let tempo = content.tempo || null;
        if (!tempo && mediaFileName) {
          tempo = extractBpmFromFilename(mediaFileName);


        }

        layers[layer] = {
          file: null,
          fileName,
          existingFile: true,
          existingFileUrl: content.file, // Store full URL/path
          mediaFile: null,
          mediaFileName,
          mediaType,
          existingMedia: hasVideo || hasAudio,
          existingMediaUrl: mediaUrl, // Store full URL/path
          tempo,
          mediaOffset: hasVideo ? (content.videoOffset || 0) : (content.audioOffset || 0),
          zoomRegions: content.zoomRegions,
        };
      }

      // Extract intro layer data
      const introLayer = existingCapsule.layers.intro;
      const introVideoUrl = introLayer?.video;
      const introVideoFileName = introVideoUrl
        ? (introLayer?.originalVideoFileName ||
           (introVideoUrl.startsWith('http')
             ? decodeURIComponent(introVideoUrl.split('/').pop() || '')
             : introVideoUrl))
        : '';

      setCapsule({
        name: existingCapsule.metadata.title,
        slug: existingCapsule.slug,
        layers,
        metadata: {
          title: existingCapsule.metadata.title,
          artist: existingCapsule.metadata.artist || '',
          composer: existingCapsule.metadata.composer || '',
          arranger: existingCapsule.metadata.arranger || '',
          difficulty: existingCapsule.metadata.difficulty || 3,
          tags: existingCapsule.metadata.tags?.join(', ') || '',
          notes: existingCapsule.metadata.notes || '',
        },
        intro: {
          videoFile: null,
          videoFileName: introVideoFileName,
          existingVideo: Boolean(introVideoUrl),
          existingVideoUrl: introVideoUrl,
          description: introLayer?.description || '',
        },
      });
    }
  }, [isEditMode, editSlug, loadingCapsule, getCapsuleBySlug]);

  const getMappedLayersCount = () => Object.keys(capsule.layers).filter(l => l !== 'intro').length;

  const canProceedToMetadata = getMappedLayersCount() > 0;
  const canProceedToPreview = capsule.metadata.title.trim() !== '';

  /** Build a temporary Capsule object for IntroView preview */
  const buildPreviewCapsule = useCallback((): Capsule => {
    const introVideoUrl = capsule.intro.videoFile
      ? URL.createObjectURL(capsule.intro.videoFile)
      : capsule.intro.existingVideoUrl || undefined;

    const layers: Capsule['layers'] = {};

    // Add intro layer if we have content
    if (introVideoUrl || capsule.intro.description) {
      layers.intro = {
        file: '__intro__',
        video: introVideoUrl,
        description: capsule.intro.description || undefined,
      };
    }

    // Add other layers (so IntroView can show "Available Layers")
    for (const [layerName, layerState] of Object.entries(capsule.layers)) {
      if (!layerState || layerName === 'intro') continue;
      layers[layerName as LayerName] = { file: layerState.fileName };
    }

    return {
      id: 'preview',
      slug: capsule.slug || 'preview',
      layers,
      metadata: {
        title: capsule.metadata.title,
        artist: capsule.metadata.artist || undefined,
        composer: capsule.metadata.composer || undefined,
        arranger: capsule.metadata.arranger || undefined,
        difficulty: capsule.metadata.difficulty as Capsule['metadata']['difficulty'],
        tags: capsule.metadata.tags
          ? capsule.metadata.tags.split(',').map(t => t.trim()).filter(Boolean)
          : undefined,
        notes: capsule.metadata.notes || undefined,
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }, [capsule]);

  return (
    <div className="curation-tool">
      {/* Header */}
      <header className="curation-header">
        {isEditMode && (
          <button
            className="back-link"
            onClick={() => navigate('/admin/capsules')}
          >
            ← Back to Capsules
          </button>
        )}
        <h1 className="curation-title">
          {isEditMode ? `Edit Capsule: ${capsule.metadata.title || editSlug}` : 'Capsule Curation Tool'}
        </h1>
        <p className="curation-subtitle">
          {isEditMode
            ? 'Modify metadata, replace files, or adjust audio sync'
            : 'Create new capsules by uploading score files and assigning them to layers'
          }
        </p>
      </header>

      {/* Draft Restore Banner */}
      {showDraftBanner && (
        <div className="draft-banner">
          <span className="draft-banner-text">You have an unsaved draft.</span>
          <button className="btn btn-sm btn-primary" onClick={handleRestoreDraft}>
            Restore
          </button>
          <button className="btn btn-sm btn-secondary" onClick={handleDiscardDraft}>
            Discard
          </button>
        </div>
      )}

      {/* Step Navigation */}
      <nav className="curation-steps">
        {steps.map((step, index) => {
          const isActive = currentStep === step.id;
          const isCompleted =
            (step.id === 'files' && getMappedLayersCount() > 0) ||
            (step.id === 'metadata' && canProceedToPreview) ||
            (step.id === 'preview' && canProceedToPreview);
          const isDisabled =
            (step.id === 'metadata' && !canProceedToMetadata) ||
            (step.id === 'preview' && !canProceedToPreview);

          return (
            <button
              key={step.id}
              className={`curation-step-btn ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
              onClick={() => !isDisabled && setCurrentStep(step.id)}
              disabled={isDisabled}
            >
              <span className="step-number">{step.number}</span>
              <span className="step-label">{step.label}</span>
              {isCompleted && !isActive && (
                <svg className="step-check" width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
              {index < steps.length - 1 && <span className="step-connector" />}
            </button>
          );
        })}
      </nav>

      {/* Content Area */}
      <main className="curation-content">
        {/* Intro Step */}
        {currentStep === 'intro' && (
          <div className="step-content intro-step">
            <h2>Intro Content</h2>
            <p className="section-description">
              Add an optional performance video and description for the capsule introduction
            </p>

            <div className="intro-step-layout">
              {/* Video upload */}
              <div className="form-group">
                <label>Performance Video (optional)</label>
                <input
                  ref={introVideoInputRef}
                  type="file"
                  accept=".mp4,.webm,.mov"
                  onChange={handleIntroVideoSelect}
                  style={{ display: 'none' }}
                />
                {capsule.intro.videoFileName ? (
                  <div className="layer-audio-info" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: 'var(--space-sm) 0' }}>
                    <span>{capsule.intro.videoFileName}</span>
                    {capsule.intro.existingVideo && <span className="field-hint">(existing)</span>}
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={handleRemoveIntroVideo}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => introVideoInputRef.current?.click()}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    Upload Video
                  </button>
                )}
                <span className="field-hint">A performance video shown on the intro page (MP4, WEBM, MOV)</span>
              </div>

              {/* Description textarea */}
              <div className="form-group full-width">
                <label htmlFor="intro-description">Description (optional)</label>
                <textarea
                  id="intro-description"
                  className="input textarea"
                  value={capsule.intro.description}
                  onChange={(e) => handleIntroDescriptionChange(e.target.value)}
                  placeholder="Describe this arrangement, what the student will learn, or any notes about the song..."
                  rows={5}
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setCurrentStep('files')}
                >
                  Continue to Files
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Files Step */}
        {currentStep === 'files' && (
          <div className="step-content files-step">
            <div className="files-layout">
              {/* File Upload Section */}
              <div className="file-upload-section">
                <h2>Upload Score File</h2>
                <p className="section-description">
                  Select a Guitar Pro (.gp, .gp5, .gpx) or MusicXML (.xml, .musicxml) file
                </p>

                <input
                  ref={mediaInputRef}
                  type="file"
                  accept=".mp3,.wav,.m4a,.ogg,.mp4,.webm,.mov"
                  onChange={handleMediaSelect}
                  style={{ display: 'none' }}
                />
                <input
                  ref={replaceFileInputRef}
                  type="file"
                  accept=".gp,.gp3,.gp4,.gp5,.gpx,.xml,.musicxml"
                  onChange={handleReplaceFileSelect}
                  className="file-input"
                  style={{ display: 'none' }}
                />
                <div className="file-input-wrapper">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".gp,.gp3,.gp4,.gp5,.gpx,.xml,.musicxml"
                    onChange={handleFileSelect}
                    id="score-file-input"
                    className="file-input"
                  />
                  <label htmlFor="score-file-input" className="file-input-label">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <span>Choose File</span>
                  </label>
                </div>

                {mergeNotification && (
                  <div style={{
                    padding: 'var(--space-sm) var(--space-md)',
                    background: 'var(--color-accent-subtle)',
                    border: '1px solid var(--color-accent)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--color-accent)',
                    fontSize: '13px',
                    marginTop: 'var(--space-sm)',
                    animation: 'fadeIn 0.2s ease',
                  }}>
                    {mergeNotification}
                  </div>
                )}

                {selectedFile && (
                  <div className="selected-file">
                    <div className="selected-file-info">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                      </svg>
                      <span className="file-name">{selectedFile.name}</span>
                    </div>

                    <div className="layer-assignment">
                      <p className="assignment-label">Assign to layer:</p>
                      <div className="layer-buttons">
                        {LAYER_ORDER.filter(l => l !== 'intro').map((layer) => (
                          <button
                            key={layer}
                            className={`layer-assign-btn layer-${layer}`}
                            onClick={() => handleAssignToLayer(layer)}
                            disabled={!!capsule.layers[layer]}
                          >
                            {LAYER_LABELS[layer]}
                            {capsule.layers[layer] && (
                              <span className="already-assigned">(assigned)</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Mapped Layers Section */}
              <div className="mapped-layers-section">
                <h2>Mapped Layers</h2>
                <p className="section-description">
                  {getMappedLayersCount()} of {LAYER_ORDER.length - 1} layers assigned
                </p>

                {validationErrors.layers && (
                  <div className="validation-error-block">{validationErrors.layers}</div>
                )}

                <div className="mapped-layers-list">
                  {LAYER_ORDER.filter(l => l !== 'intro').map((layer) => {
                    const layerData = capsule.layers[layer];
                    const existingCapsule = editSlug ? getCapsuleBySlug(editSlug) : null;
                    const originalFileName = existingCapsule?.layers[layer]?.file;
                    const isPendingReplacement = layerData?.existingFile && layerData.file !== null;

                    return (
                      <div
                        key={layer}
                        className={`mapped-layer-item ${layerData ? 'assigned' : 'empty'} layer-${layer}`}
                      >
                        <div className="layer-indicator" />
                        <div className="layer-info">
                          <span className="layer-name">{LAYER_LABELS[layer]}</span>
                          {layerData && (
                            <div className="layer-file-row">
                              <span className="layer-file">
                                {layerData.fileName}
                                {isPendingReplacement && originalFileName && (
                                  <span className="replacing-text"> (replacing {originalFileName})</span>
                                )}
                              </span>
                              {layerData.existingFile && !layerData.file && (
                                <button
                                  className="btn-small btn-secondary"
                                  onClick={() => handleReplaceClick(layer)}
                                  title="Replace this file"
                                >
                                  Replace
                                </button>
                              )}
                              {isPendingReplacement && (
                                <button
                                  className="btn-small btn-secondary"
                                  onClick={() => handleUndoReplace(layer)}
                                  title="Undo replacement"
                                >
                                  Undo
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        {layerData && (
                          <button
                            className="remove-layer-btn"
                            onClick={() => handleRemoveLayer(layer)}
                            title="Remove layer"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        )}
                        {/* Media Section (Audio/Video) - only shown when layer has a score file */}
                        {layerData && (
                          <div className="layer-audio-section">
                            {layerData.mediaFileName ? (
                              <div className="layer-audio-info">
                                <span className="audio-icon">{layerData.mediaType === 'video' ? '🎬' : '🎵'}</span>
                                <span className="audio-filename">{layerData.mediaFileName}</span>
                                {layerData.mediaOffset !== 0 && (
                                  <span className="audio-offset">
                                    ({layerData.mediaOffset > 0 ? '+' : ''}{layerData.mediaOffset}ms)
                                  </span>
                                )}
                                {/* Sync button for both audio and video */}
                                <button
                                  className="audio-action-btn"
                                  onClick={() => handleSyncClick(layer)}
                                  title={layerData.mediaOffset !== 0 ? "Re-sync" : "Sync"}
                                >
                                  {layerData.mediaOffset !== 0 ? 'Re-sync' : 'Sync'}
                                </button>
                                <button
                                  className="audio-remove-btn"
                                  onClick={() => handleRemoveMedia(layer)}
                                  title={`Remove ${layerData.mediaType || 'media'}`}
                                >
                                  ×
                                </button>
                              </div>
                            ) : (
                              <button
                                className="add-audio-btn"
                                onClick={() => handleAddMediaClick(layer)}
                              >
                                + Add Audio/Video
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="files-step-actions">
                  {canProceedToMetadata && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleSave(true)}
                      disabled={!!uploadProgress}
                      title="Save current progress without completing all fields"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                        <polyline points="17 21 17 13 7 13 7 21" />
                        <polyline points="7 3 7 8 15 8" />
                      </svg>
                      Save Draft
                    </button>
                  )}
                  {canProceedToMetadata && (
                    <button
                      className="btn btn-primary next-step-btn"
                      onClick={() => setCurrentStep('metadata')}
                    >
                      Continue to Metadata
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Metadata Step */}
        {currentStep === 'metadata' && (
          <div className="step-content metadata-step">
            <h2>Capsule Metadata</h2>
            <p className="section-description">
              Fill in the details about this arrangement
            </p>

            <form className="metadata-form" onSubmit={(e) => e.preventDefault()}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="title">Title *</label>
                  <input
                    type="text"
                    id="title"
                    className={`input ${validationErrors.title ? 'input-error' : ''}`}
                    value={capsule.metadata.title}
                    onChange={(e) => handleMetadataChange('title', e.target.value)}
                    placeholder="e.g., Blackbird"
                  />
                  {validationErrors.title && (
                    <span className="validation-error">{validationErrors.title}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="slug">Slug (URL-safe name)</label>
                  <div className={`slug-input-wrapper ${isEditMode ? 'locked' : ''}`}>
                    {isEditMode && <span className="slug-lock-icon">🔒</span>}
                    <input
                      type="text"
                      id="slug"
                      className="input"
                      value={capsule.slug}
                      onChange={(e) => handleSlugChange(e.target.value)}
                      placeholder="auto-generated from title"
                      disabled={isEditMode}
                    />
                  </div>
                  <span className="field-hint">
                    {isEditMode
                      ? 'Locked after creation to preserve URLs'
                      : 'Used in file paths and URLs'
                    }
                  </span>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="artist">Artist</label>
                  <input
                    type="text"
                    id="artist"
                    className="input"
                    value={capsule.metadata.artist}
                    onChange={(e) => handleMetadataChange('artist', e.target.value)}
                    placeholder="e.g., The Beatles"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="composer">Composer</label>
                  <input
                    type="text"
                    id="composer"
                    className="input"
                    value={capsule.metadata.composer}
                    onChange={(e) => handleMetadataChange('composer', e.target.value)}
                    placeholder="e.g., Paul McCartney"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="arranger">Arranger</label>
                  <input
                    type="text"
                    id="arranger"
                    className="input"
                    value={capsule.metadata.arranger}
                    onChange={(e) => handleMetadataChange('arranger', e.target.value)}
                    placeholder="e.g., Hagai Rehavia"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="difficulty">Difficulty (1-5)</label>
                  <div className="difficulty-selector">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <button
                        key={level}
                        type="button"
                        className={`difficulty-btn ${capsule.metadata.difficulty === level ? 'active' : ''}`}
                        onClick={() => handleMetadataChange('difficulty', level)}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                  <span className="field-hint">
                    {capsule.metadata.difficulty === 1 && 'Beginner'}
                    {capsule.metadata.difficulty === 2 && 'Easy'}
                    {capsule.metadata.difficulty === 3 && 'Intermediate'}
                    {capsule.metadata.difficulty === 4 && 'Advanced'}
                    {capsule.metadata.difficulty === 5 && 'Expert'}
                  </span>
                </div>
              </div>

              <div className="form-group full-width">
                <label htmlFor="tags">Tags</label>
                <input
                  type="text"
                  id="tags"
                  className="input"
                  value={capsule.metadata.tags}
                  onChange={(e) => handleMetadataChange('tags', e.target.value)}
                  placeholder="e.g., fingerstyle, acoustic, beginner-friendly (comma separated)"
                />
              </div>

              <div className="form-group full-width">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  className="input textarea"
                  value={capsule.metadata.notes}
                  onChange={(e) => handleMetadataChange('notes', e.target.value)}
                  placeholder="Any additional notes about this arrangement..."
                  rows={3}
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setCurrentStep('files')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                  Back to Files
                </button>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleSave(true)}
                    disabled={!!uploadProgress || !capsule.metadata.title.trim()}
                    title="Save current progress without completing all fields"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                      <polyline points="17 21 17 13 7 13 7 21" />
                      <polyline points="7 3 7 8 15 8" />
                    </svg>
                    Save Draft
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setCurrentStep('preview')}
                    disabled={!canProceedToPreview}
                  >
                    Continue to Preview
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Preview Step */}
        {currentStep === 'preview' && (
          <div className="step-content preview-step">
            <h2>Preview & Save</h2>
            <p className="section-description">
              Review your capsule before saving
            </p>

            <div className="preview-card">
              <div className="preview-header">
                <h3 className="preview-title">{capsule.metadata.title || 'Untitled'}</h3>
                <span className="preview-slug">/{capsule.slug || 'no-slug'}</span>
              </div>

              <div className="preview-meta">
                {capsule.metadata.artist && (
                  <div className="meta-item">
                    <span className="meta-label">Artist:</span>
                    <span className="meta-value">{capsule.metadata.artist}</span>
                  </div>
                )}
                {capsule.metadata.composer && (
                  <div className="meta-item">
                    <span className="meta-label">Composer:</span>
                    <span className="meta-value">{capsule.metadata.composer}</span>
                  </div>
                )}
                {capsule.metadata.arranger && (
                  <div className="meta-item">
                    <span className="meta-label">Arranger:</span>
                    <span className="meta-value">{capsule.metadata.arranger}</span>
                  </div>
                )}
                <div className="meta-item">
                  <span className="meta-label">Difficulty:</span>
                  <span className="meta-value difficulty-badge">
                    {'*'.repeat(capsule.metadata.difficulty)}
                  </span>
                </div>
              </div>

              {capsule.metadata.tags && (
                <div className="preview-tags">
                  {capsule.metadata.tags.split(',').map((tag, i) => (
                    <span key={i} className="preview-tag">
                      {tag.trim()}
                    </span>
                  ))}
                </div>
              )}

              {capsule.metadata.notes && (
                <div className="preview-notes">
                  <span className="meta-label">Notes:</span>
                  <p>{capsule.metadata.notes}</p>
                </div>
              )}

              <div className="preview-layers">
                <h4>Layers ({getMappedLayersCount()})</h4>
                <div className="preview-layers-list">
                  {LAYER_ORDER.filter(l => l !== 'intro').map((layer) => {
                    const layerData = capsule.layers[layer];
                    if (!layerData) return null;
                    return (
                      <div key={layer} className={`preview-layer layer-${layer}`}>
                        <span className="layer-indicator" />
                        <span className="layer-name">{LAYER_LABELS[layer]}</span>
                        <span className="layer-file">{layerData.fileName}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Intro Preview */}
            {(capsule.intro.videoFile || capsule.intro.existingVideo || capsule.intro.description) && (
              <div className="preview-intro-section">
                <h4>Intro Preview</h4>
                <p className="section-description">
                  This is how learners will see the intro page
                </p>
                <div className="preview-intro-frame">
                  <IntroView
                    capsule={buildPreviewCapsule()}
                    onStartLearning={() => {}}
                  />
                </div>
              </div>
            )}

            <div className="form-actions-column">
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setCurrentStep('metadata')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                  Back to Metadata
                </button>

                <button
                  type="button"
                  className="btn btn-primary btn-lg"
                  onClick={() => handleSave(false)}
                  disabled={!!uploadProgress || hasValidationErrors}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                    <polyline points="17 21 17 13 7 13 7 21" />
                    <polyline points="7 3 7 8 15 8" />
                  </svg>
                  {uploadProgress ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Save Capsule')}
                </button>
              </div>

              {/* Upload Progress Area */}
              {(uploadProgress || Object.keys(fileUploadProgress).length > 0 || retryStatus) && (
                <div className="upload-progress-area">
                  {uploadProgress && (
                    <div className="upload-progress-status">{uploadProgress}</div>
                  )}
                  {retryStatus && (
                    <div className="upload-retry-status">{retryStatus}</div>
                  )}
                  {Object.entries(fileUploadProgress).map(([key, percent]) => (
                    <div key={key} className="upload-progress-item">
                      <span className="upload-progress-label">{key.replace(/_/g, ' ')}</span>
                      <div className="upload-progress-bar-track">
                        <div
                          className="upload-progress-bar-fill"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <span className="upload-progress-percent">{percent}%</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Save Error with Try Again */}
              {saveError && (
                <div className="save-error">
                  <span className="save-error-text">Error: {saveError}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => handleSave(false)}
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default CurationTool;
